import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core/';
import { ISubtaskType } from '../../../../models/subtaskType.model';
import {
  addSelectedSubtaskTypeInTaskCreate,
  TaskCreateState,
} from '../../../../state/taskCreate';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../state/configureStore';
import {
  Close as CloseIcon,
  SkipNext as NextIcon,
  SkipPrevious as PreviousIcon,
} from '@material-ui/icons';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      padding: 0,
      background:
        'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 30%), linear-gradient(135deg, #072577 0%, #1d4ed8 55%, #60a5fa 100%)',
    },
    titleContent: {
      padding: theme.spacing(2.25, 3, 1.75),
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    titleText: {
      flex: 1,
      minWidth: 0,
    },
    titleHeading: {
      color: '#f8fafc',
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    titleSubtitle: {
      color: 'rgba(226, 232, 240, 0.82)',
      lineHeight: 1.65,
      maxWidth: 700,
    },
    closeButton: {
      color: '#ffffff',
      backgroundColor: 'rgba(255,255,255,0.12)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.2)',
      },
    },
    content: {
      padding: theme.spacing(2.5, 3, 2),
      background:
        'linear-gradient(180deg, rgba(248,250,252,0.94) 0%, rgba(255,255,255,1) 100%)',
    },
    selectionMeta: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    metaTitle: {
      fontWeight: 700,
      color: '#0f172a',
    },
    metaSubtitle: {
      color: '#64748b',
      lineHeight: 1.6,
    },
    selectionBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(0.85, 1.5),
      borderRadius: 999,
      fontSize: '0.82rem',
      fontWeight: 700,
      color: '#1d4ed8',
      background: 'rgba(219, 234, 254, 0.9)',
      border: '1px solid rgba(96, 165, 250, 0.35)',
    },
    selectionSurface: {
      borderRadius: 20,
      padding: theme.spacing(1.25),
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
      border: '1px solid rgba(148, 163, 184, 0.18)',
      boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08)',
    },
    optionsScrollArea: {
      maxHeight: 290,
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingRight: theme.spacing(0.5),
      '&::-webkit-scrollbar': {
        width: 8,
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(226, 232, 240, 0.7)',
        borderRadius: 999,
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(96, 165, 250, 0.85)',
        borderRadius: 999,
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: 'rgba(59, 130, 246, 0.95)',
      },
    },
    optionsGrid: {
      margin: 0,
      width: '100%',
    },
    optionLabel: {
      margin: 0,
      width: '100%',
      padding: theme.spacing(0.35, 0),
    },
    optionCard: {
      width: '100%',
      margin: 0,
      borderRadius: 16,
      border: '1px solid rgba(148, 163, 184, 0.18)',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.92) 100%)',
      transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 14px 28px rgba(15, 23, 42, 0.08)',
        borderColor: 'rgba(96, 165, 250, 0.35)',
      },
    },
    optionCardSelected: {
      borderColor: 'rgba(37, 99, 235, 0.4)',
      background:
        'linear-gradient(135deg, rgba(219,234,254,0.92) 0%, rgba(239,246,255,0.98) 100%)',
      boxShadow: '0 16px 30px rgba(37, 99, 235, 0.12)',
    },
    optionName: {
      fontWeight: 700,
      color: '#0f172a',
    },
    optionHint: {
      color: '#64748b',
      fontSize: '0.84rem',
      lineHeight: 1.55,
    },
    checkbox: {
      color: '#94a3b8',
      '&.Mui-checked': {
        color: '#2563eb',
      },
    },
    actions: {
      padding: theme.spacing(2, 3, 2),
      justifyContent: 'space-between',
      alignItems: 'center',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(248,250,252,0.96) 40%)',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: theme.spacing(1),
      },
    },
    actionText: {
      color: '#64748b',
      fontSize: '0.88rem',
      lineHeight: 1.6,
      maxWidth: 420,
    },
    actionShell: {
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75),
      borderRadius: 999,
      border: '1px solid rgba(148, 163, 184, 0.18)',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.88) 100%)',
      boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    secondaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
    primaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2.5),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 55%, #60a5fa 100%)',
      boxShadow: '0 14px 30px rgba(37, 99, 235, 0.24)',
      '&:hover': {
        background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 55%, #3b82f6 100%)',
        boxShadow: '0 16px 34px rgba(37, 99, 235, 0.28)',
      },
      '&.Mui-disabled': {
        color: 'rgba(255,255,255,0.72)',
        background: 'linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)',
      },
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
  }),
);

interface SubtaskTypeSelectDialogProps {
  nextClick: () => void;
  backClick: () => void;
  closeClick: () => void;
  subtaskTypeList: ISubtaskType[];
}

const SubtaskTypeSelectDialog: React.FC<SubtaskTypeSelectDialogProps> = (props) => {
  const dispatch = useDispatch();
  const { nextClick, backClick, subtaskTypeList, closeClick } = props;
  const { selectedSubtaskType } = useSelector<AppState, TaskCreateState>(
    (state) => state.taskCreate,
  );
  const classes = useStyles();
  const [selectedSubtaskTypeNames, setSelectedSubTaskTypeNames] = useState<Set<string>>(
    new Set<string>(),
  );

  useEffect(() => {
    setSelectedSubTaskTypeNames(new Set<string>(selectedSubtaskType));
  }, [selectedSubtaskType, setSelectedSubTaskTypeNames]);

  return (
    <>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.titleContent}>
          <Box className={classes.titleText}>
            <Typography variant="h4" className={classes.titleHeading}>
              Select Subtask Type
            </Typography>
            <Typography variant="body1" className={classes.titleSubtitle}>
              Choose the types of subtasks that are relevant for this task to create.
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => {
              closeClick();
            }}
            className={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Box className={classes.selectionMeta}>
          <Box>
            <Typography variant="subtitle1" className={classes.metaTitle}>
              Available types
            </Typography>
            <Typography variant="body2" className={classes.metaSubtitle}>
              Pick one or more options to continue with the task setup.
            </Typography>
          </Box>
          <Box className={classes.selectionBadge}>
            {selectedSubtaskTypeNames.size} selected
          </Box>
        </Box>
        <Box className={classes.selectionSurface}>
          <Box className={classes.optionsScrollArea}>
            <Grid container spacing={2} className={classes.optionsGrid}>
              <FormControl component="fieldset" fullWidth>
                <FormGroup>
                  {subtaskTypeList.map((subtaskType) => {
                    const isSelected = selectedSubtaskTypeNames.has(
                      subtaskType.name ?? '',
                    );
                    return (
                      <FormControlLabel
                        key={subtaskType.id}
                        className={classes.optionLabel}
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              const newSelectedSet = new Set(selectedSubtaskTypeNames);
                              if (e.target.checked) {
                                newSelectedSet.add(e.target.value);
                              } else {
                                newSelectedSet.delete(e.target.value);
                              }
                              setSelectedSubTaskTypeNames(newSelectedSet);
                            }}
                            className={classes.checkbox}
                            value={subtaskType.name}
                          />
                        }
                        label={
                          <Box
                            className={`${classes.optionCard} ${
                              isSelected ? classes.optionCardSelected : ''
                            }`}
                          >
                            <Box px={2} py={1.4}>
                              <Typography variant="body1" className={classes.optionName}>
                                {subtaskType.name}
                              </Typography>
                              <Typography variant="body2" className={classes.optionHint}>
                                Include this type in the task creation flow.
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    );
                  })}
                </FormGroup>
              </FormControl>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Typography className={classes.actionText}>
          Select at least one subtask type to unlock the next step and keep the setup
          focused.
        </Typography>
        <Box>
          <Button
            onClick={() => {
              backClick();
            }}
            startIcon={<PreviousIcon />}
            variant="outlined"
            color="primary"
            className={classes.secondaryButton}
          >
            Back
          </Button>
          <Button
            onClick={() => {
              dispatch(
                addSelectedSubtaskTypeInTaskCreate(Array.from(selectedSubtaskTypeNames)),
              );
              nextClick();
            }}
            disabled={selectedSubtaskTypeNames.size === 0}
            variant="contained"
            color="primary"
            startIcon={<NextIcon />}
            className={classes.primaryButton}
          >
            Next
          </Button>
        </Box>
      </DialogActions>
    </>
  );
};

export default SubtaskTypeSelectDialog;
